import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import SearchBar from '../../components/JobBoard/SearchBar';
import FilterSidebar from '../../components/JobBoard/FilterSidebar';
import JobCard from '../../components/JobBoard/JobCard';
import JobBoardPagination from '../../components/JobBoard/JobBoardPagination';
import JobListHeader from '../../components/JobBoard/JobListHeader';
import { KpathportInlineBanner } from '../../components/JobBoard/KpathportBanner';
import HiringBanner from '../../components/JobBoard/HiringBanner';
import SkeletonJobCard from '../../components/JobBoard/SkeletonJobCard';
import JobHeader from '../../components/JobBoard/JobHeader';
import SearchNotFound from '../../components/JobBoard/SearchNotFound';
import AdminSearchBar from '../../components/JobBoard/Admin/AdminSearchBar';
import AdminSidebar, {
  MENU_ITEMS as ADMIN_STATUS_ITEMS,
} from '../../components/JobBoard/Admin/AdminSidebar';
import AdminMobileMenuDrawer from '../../components/JobBoard/Admin/AdminMobileMenuDrawer';
import AdminJobCard from '../../components/JobBoard/Admin/AdminJobCard';
import CreateJobBanner from '../../components/JobBoard/CreateJobBanner';
import { ReactComponent as NavigationLeftIcon } from '@repo/common/assets/icons/24px/icon_24px_navigation-left.svg';
import {
  bookmarkJobPost,
  changeJobPostStatus,
  deleteJobPost,
  deleteJobPostPermanent,
  duplicateJobPost,
  closeJobPost,
  getJobPostDetail,
  getJobPostOptions,
  getJobPosts,
  getJobFeed,
  hideJobPost,
  hideJobPostForUser,
  unbookmarkJobPost,
  unhideJobPost,
  unhideJobPostForUser,
} from '../../api/jobboard/jobboard.api';
import {
  buildJobOptionMeta,
  getCloseStatus,
  getHideStatus,
  getTrashStatus,
  mapJobPostDetailToCardModel,
  mapJobPostListToCardModel,
  isAnyCareerLevel,
  matchesLocationSelection,
  matchesVisaSelection,
  Toast,
  Modal,
  useAuth,
  trackJobSearch,
  useIsMobile,
  useT,
} from '@repo/common';

const PAGE_SIZE = 12;
/** 목록 중간에 배너를 삽입할 카드 인덱스(0-based). 해당 카드 "뒤"에 들어간다. */
const HIRING_BANNER_INDEX = 2;
const PATHFINDER_BANNER_INDEX = 5;
const ADMIN_ROLES = new Set(['A', 'ROLE_A', 'ADMIN', 'ROLE_ADMIN']);
const EMPLOYER_ROLES = new Set(['E', 'ROLE_E', 'EMPLOYER', 'ROLE_EMPLOYER']);
const normalizeLabel = (value) =>
  String(value || '')
    .trim()
    .toLowerCase();
const matchesLabel = (value, selected) => {
  const normalizedValue = normalizeLabel(value);
  const normalizedSelected = normalizeLabel(selected);
  if (!normalizedValue || !normalizedSelected) return false;
  return (
    normalizedValue.includes(normalizedSelected) ||
    normalizedSelected.includes(normalizedValue)
  );
};
const includesAny = (list, selections) =>
  (selections || []).some((selected) =>
    (list || []).some((item) => matchesLabel(item, selected)),
  );

const NON_ADMIN_VISIBLE_STATUSES = new Set(['ACTIVE', 'CLOSED']);

const hasAppliedJobFilters = (filters = {}) =>
  Object.entries(filters).some(([key, value]) =>
    key === 'e7Support'
      ? Boolean(value)
      : Array.isArray(value) && value.length > 0,
  );

const JobBoard = ({ management = false }) => {
  const t = useT();
  const location = useLocation();
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({});
  const [sortBy, setSortBy] = useState('By Date');
  const [searchParams, setSearchParams] = useState({
    jobTitle: '',
    location: '',
  });
  const [adminSearchTerm, setAdminSearchTerm] = useState('');
  const [pendingTrack, setPendingTrack] = useState(null);

  const [adminStatus, setAdminStatus] = useState('active');
  const [showToast, setShowToast] = useState(false);
  const [showSaveToast, setShowSaveToast] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isFilterLoading, setIsFilterLoading] = useState(false);
  const [selectedJobIds, setSelectedJobIds] = useState(new Set());
  const [pageToast, setPageToast] = useState(null);
  const [activeToast, setActiveToast] = useState(null);
  const [activeModal, setActiveModal] = useState(null);
  const [pendingJob, setPendingJob] = useState(null);
  const [pendingJobs, setPendingJobs] = useState([]);
  const [pendingUndoAction, setPendingUndoAction] = useState(null);
  const [jobs, setJobs] = useState([]);
  const filterLoadingTimeoutRef = useRef(null);

  const { user } = useAuth();
  const userRole = user?.role ?? null;
  const normalizedRole = String(userRole ?? '').toUpperCase();
  const isAdmin = ADMIN_ROLES.has(normalizedRole);
  const isEmployer = EMPLOYER_ROLES.has(normalizedRole);
  const canManage = isAdmin || isEmployer;
  const isManager = management && canManage;
  const CardComponent = isManager ? AdminJobCard : JobCard;

  /* 모바일 공고 관리: 상태 사이드바는 햄버거 드로어로, 검색은 상단 고정 영역으로 옮긴다 */
  const isMobile = useIsMobile();
  const isMobileManager = isManager && isMobile;
  const [isAdminMenuOpen, setIsAdminMenuOpen] = useState(false);
  const showFilterSidebar = !(isManager && isEmployer);
  // 모바일 공고 관리(관리자)는 필터를 검색바 아래 칩 바로 올리므로 좌측 컬럼이 없다
  const showSideColumn =
    (isManager && !isMobile) || (showFilterSidebar && !isMobileManager);
  const adminStatusItem = ADMIN_STATUS_ITEMS.find(
    (item) => item.id === adminStatus,
  );

  const fetchJobPosts = async () => {
    try {
      setIsLoading(true);
      const [listData, optionGroups] = await Promise.all([
        isManager ? getJobPosts() : getJobFeed(),
        getJobPostOptions(),
      ]);
      const optionMeta = buildJobOptionMeta(optionGroups || []);

      const enriched = await Promise.all(
        (listData || []).map(async (post) => {
          try {
            const detail = await getJobPostDetail(post.id);
            return mapJobPostDetailToCardModel(detail, optionMeta);
          } catch (error) {
            console.error(`Failed to fetch job detail(${post.id}):`, error);
            return mapJobPostListToCardModel(post);
          }
        }),
      );

      setJobs(enriched);
    } catch (error) {
      console.error('Failed to fetch job posts:', error);
      setJobs([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchJobPosts();
    return () => {
      if (filterLoadingTimeoutRef.current) {
        clearTimeout(filterLoadingTimeoutRef.current);
      }
    };
  }, [isManager, user?.id]);

  useEffect(() => {
    if (location.state?.from === 'resume-success') {
      setShowToast(true);
      requestAnimationFrame(() => {
        window.scrollTo({ top: 70, behavior: 'auto' });
      });
    } else {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, [location]);

  useEffect(() => {
    setSelectedJobIds(new Set());
  }, [adminStatus]);

  useEffect(() => {
    if (location.state?.toast) {
      setPageToast(location.state.toast);
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate]);

  useEffect(() => {
    setSelectedJobIds(new Set());
  }, [adminStatus, currentPage]);

  const adminCounts = useMemo(
    () => ({
      all: jobs.filter((job) => !job.status?.startsWith('DELETED_')).length,
      active: jobs.filter((job) => job.status === 'ACTIVE').length,
      hidden: jobs.filter((job) => job.status === 'HIDDEN').length,
      drafts: jobs.filter((job) => job.status === 'DRAFT').length,
      closed: jobs.filter((job) => job.status === 'CLOSED').length,
      trash: jobs.filter((job) => job.status?.startsWith('DELETED_')).length,
    }),
    [jobs],
  );

  const filteredJobs = useMemo(() => {
    let list = jobs.filter((job) => {
      if (!isManager) return NON_ADMIN_VISIBLE_STATUSES.has(job.status);

      switch (adminStatus) {
        case 'all':
          return !job.status?.startsWith('DELETED_');
        case 'active':
          return job.status === 'ACTIVE';
        case 'hidden':
          return job.status === 'HIDDEN';
        case 'drafts':
          return job.status === 'DRAFT';
        case 'closed':
          return job.status === 'CLOSED';
        case 'trash':
          return job.status?.startsWith('DELETED_');
        default:
          return true;
      }
    });

    if (isManager) {
      const keyword = adminSearchTerm.trim().toLowerCase();
      if (keyword) {
        list = list.filter((job) =>
          [job.title, job.company, job.location, ...(job.keywords || [])]
            .filter(Boolean)
            .join(' ')
            .toLowerCase()
            .includes(keyword),
        );
      }
    } else {
      const keyword = searchParams.jobTitle?.trim().toLowerCase();
      const locationKeyword = searchParams.location?.trim().toLowerCase();
      if (keyword) {
        list = list.filter((job) =>
          [job.title, job.company]
            .filter(Boolean)
            .join(' ')
            .toLowerCase()
            .includes(keyword),
        );
      }
      if (locationKeyword) {
        list = list.filter((job) =>
          (job.location || '').toLowerCase().includes(locationKeyword),
        );
      }
    }

    if (Boolean(filters?.[1]?.length)) {
      list = list.filter((job) => job.hasE7Support);
    }

    const selectedLocations = filters?.[2] || [];
    if (selectedLocations.length) {
      list = list.filter((job) =>
        selectedLocations.some((locationValue) =>
          matchesLocationSelection(job, locationValue),
        ),
      );
    }

    const selectedLanguages = filters?.[3] || [];
    if (selectedLanguages.length) {
      list = list.filter((job) =>
        includesAny(job?.requiredLanguageLabels, selectedLanguages),
      );
    }

    const selectedRoles = filters?.[4] || [];
    if (selectedRoles.length) {
      list = list.filter((job) =>
        selectedRoles.some((role) => matchesLabel(job?.role, role)),
      );
    }

    const selectedCareerLevels = filters?.[5] || [];
    /*
     * 'Any experience level'을 고르면 경력 조건 자체를 걸지 않는다.
     * 다른 경력을 함께 골랐더라도 '무관'이 더 넓은 조건이라 그쪽을 따른다.
     */
    const wantsAnyCareerLevel = selectedCareerLevels.some(isAnyCareerLevel);

    if (selectedCareerLevels.length && !wantsAnyCareerLevel) {
      list = list.filter(
        (job) =>
          !String(job?.careerLevel ?? '').trim() ||
          selectedCareerLevels.some((career) =>
            matchesLabel(job?.careerLevel, career),
          ),
      );
    }

    const selectedVisaTypes = filters?.[6] || [];
    if (selectedVisaTypes.length) {
      list = list.filter((job) =>
        selectedVisaTypes.some((visaType) =>
          matchesVisaSelection(job, visaType),
        ),
      );
    }

    const selectedEmploymentTypes = filters?.[7] || [];
    if (selectedEmploymentTypes.length) {
      list = list.filter((job) =>
        selectedEmploymentTypes.some((type) =>
          matchesLabel(job?.employmentType, type),
        ),
      );
    }

    const sorted = [...list].sort((a, b) => {
      if (sortBy === 'By Deadline') {
        const deadlineA = a.applicationDeadline
          ? new Date(a.applicationDeadline).getTime()
          : Number.MAX_SAFE_INTEGER;
        const deadlineB = b.applicationDeadline
          ? new Date(b.applicationDeadline).getTime()
          : Number.MAX_SAFE_INTEGER;
        return deadlineA - deadlineB;
      }

      const createdA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const createdB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return createdB - createdA;
    });

    return sorted;
  }, [
    jobs,
    isManager,
    adminStatus,
    adminSearchTerm,
    searchParams,
    sortBy,
    filters,
  ]);

  useEffect(() => {
    if (!pendingTrack) return;

    trackJobSearch({
      keyword: searchParams.jobTitle,
      location: searchParams.location,
      filter_used: pendingTrack === 'filter',
      results_count: filteredJobs.length,
      e7_filter: Boolean(filters?.e7Support || filters?.[1]?.length),
    });

    setPendingTrack(null);
  }, [filteredJobs, pendingTrack]);

  const totalPages = Math.max(1, Math.ceil(filteredJobs.length / PAGE_SIZE));
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const pagedJobs = filteredJobs.slice(startIndex, startIndex + PAGE_SIZE);
  const isSearchApplied = isManager
    ? Boolean(adminSearchTerm.trim())
    : Boolean(searchParams.jobTitle.trim() || searchParams.location.trim());
  const isFilterApplied = hasAppliedJobFilters(filters) || isSearchApplied;

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  const handleToggleBookmark = async (jobId, nextBookmarked) => {
    if (!jobId) return;

    if (nextBookmarked) {
      await bookmarkJobPost(jobId);
    } else {
      await unbookmarkJobPost(jobId);
    }

    setJobs((prev) =>
      prev.map((job) =>
        job.id === jobId ? { ...job, isBookmarked: nextBookmarked } : job,
      ),
    );
  };

  const handleToggleHiddenForUser = async (jobId, nextHidden) => {
    if (!jobId) return;

    if (nextHidden) {
      await hideJobPostForUser(jobId);
    } else {
      await unhideJobPostForUser(jobId);
    }

    setJobs((prev) =>
      prev.map((job) =>
        job.id === jobId ? { ...job, isHiddenByUser: nextHidden } : job,
      ),
    );
  };

  const commitPendingUndoAction = async () => {
    if (!pendingUndoAction) return;

    const actionToCommit = pendingUndoAction;
    setPendingUndoAction(null);

    try {
      await actionToCommit.commit();
      await fetchJobPosts();
    } catch (error) {
      console.error(
        `Failed to commit pending action(${actionToCommit.type}):`,
        error,
      );
      await fetchJobPosts();
    }
  };

  const startUndoableAction = async ({ type, applyLocalUpdate, commit }) => {
    await commitPendingUndoAction();

    const previousJobs = structuredClone(jobs);
    setJobs((prev) => applyLocalUpdate(prev));
    setSelectedJobIds(new Set());
    setActiveToast(type);
    setPendingUndoAction({
      type,
      previousJobs,
      commit,
    });
  };

  const handleCardAction = async (action, job) => {
    if (!job) return;

    try {
      switch (action) {
        case 'edit':
          await commitPendingUndoAction();
          navigate(`/jobs/admin/${job.id}/edit`);
          return;
        case 'hide':
          await startUndoableAction({
            type: 'hide',
            applyLocalUpdate: (prev) =>
              prev.map((item) =>
                item.id === job.id
                  ? {
                      ...item,
                      status: getHideStatus(item.status),
                    }
                  : item,
              ),
            commit: () => hideJobPost(job.id),
          });
          return;
        case 'setActive':
          await commitPendingUndoAction();
          await unhideJobPost(job.id).catch(async () => {
            await changeJobPostStatus(job.id, 'ACTIVE');
          });
          setActiveToast('setActive');
          break;
        case 'reopen':
          await commitPendingUndoAction();
          await unhideJobPost(job.id).catch(async () => {
            await changeJobPostStatus(job.id, 'ACTIVE');
          });
          setActiveToast('reopen');
          break;
        case 'close':
          await startUndoableAction({
            type: 'close',
            applyLocalUpdate: (prev) =>
              prev.map((item) =>
                item.id === job.id
                  ? {
                      ...item,
                      status: getCloseStatus(item.status),
                    }
                  : item,
              ),
            commit: () => closeJobPost(job.id),
          });
          return;
        case 'delete':
          await startUndoableAction({
            type: 'delete',
            applyLocalUpdate: (prev) =>
              prev.map((item) =>
                item.id === job.id
                  ? {
                      ...item,
                      status: getTrashStatus(item.status),
                    }
                  : item,
              ),
            commit: () => deleteJobPost(job.id),
          });
          return;
        case 'deletePermanent':
          await commitPendingUndoAction();
          setPendingJobs([job]);
          setActiveModal('deletePermanent');
          return;
        case 'duplicate':
          await commitPendingUndoAction();
          await duplicateJobPost(job.id);
          setActiveToast('duplicate');
          break;
        case 'publish':
          await commitPendingUndoAction();
          setPendingJob(job);
          setActiveModal('publish');
          return;
        case 'restore':
          await commitPendingUndoAction();
          await changeJobPostStatus(job.id, 'ACTIVE');
          setActiveToast('restore');
          break;
        default:
          return;
      }

      await fetchJobPosts();
    } catch (error) {
      console.error(`Failed to handle card action(${action}):`, error);
    }
  };

  const handleToggleAll = (checked) => {
    if (checked) {
      const allIds = new Set(pagedJobs.map((job) => job.id));
      setSelectedJobIds(allIds);
      return;
    }
    setSelectedJobIds(new Set());
  };

  const isAllChecked =
    pagedJobs.length > 0 &&
    pagedJobs.every((job) => selectedJobIds.has(job.id));

  const handleToggleOne = (jobId, checked) => {
    setSelectedJobIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(jobId);
      else next.delete(jobId);
      return next;
    });
  };

  const selectedJobs = pagedJobs.filter((job) => selectedJobIds.has(job.id));

  const runBulkAction = async (action) => {
    if (!selectedJobs.length) return;

    if (action === 'deletePermanent') {
      await commitPendingUndoAction();
      setPendingJobs(selectedJobs);
      setActiveModal('deletePermanent');
      return;
    }

    try {
      const targetIds = new Set(selectedJobs.map((job) => job.id));

      if (action === 'delete') {
        await startUndoableAction({
          type: 'delete',
          applyLocalUpdate: (prev) =>
            prev.map((item) =>
              targetIds.has(item.id)
                ? {
                    ...item,
                    status: getTrashStatus(item.status),
                  }
                : item,
            ),
          commit: () =>
            Promise.all(selectedJobs.map((job) => deleteJobPost(job.id))),
        });
        return;
      }

      if (action === 'hide') {
        await startUndoableAction({
          type: 'hide',
          applyLocalUpdate: (prev) =>
            prev.map((item) =>
              targetIds.has(item.id)
                ? {
                    ...item,
                    status: getHideStatus(item.status),
                  }
                : item,
            ),
          commit: () =>
            Promise.all(selectedJobs.map((job) => hideJobPost(job.id))),
        });
        return;
      }

      await commitPendingUndoAction();

      if (action === 'unhide') {
        await Promise.all(
          selectedJobs.map((job) =>
            unhideJobPost(job.id).catch(async () => {
              await changeJobPostStatus(job.id, 'ACTIVE');
            }),
          ),
        );
      }

      setActiveToast(action);
      setSelectedJobIds(new Set());
      await fetchJobPosts();
    } catch (error) {
      console.error(`Failed to handle bulk action(${action}):`, error);
    }
  };

  const closeToast = async () => {
    const hasPendingUndo = Boolean(pendingUndoAction);
    setActiveToast(null);
    setPageToast(null);

    if (hasPendingUndo) {
      await commitPendingUndoAction();
    }
  };

  const closeModal = () => {
    setActiveModal(null);
    setPendingJob(null);
    setPendingJobs([]);
  };

  const handleUndo = () => {
    if (pendingUndoAction?.previousJobs) {
      setJobs(pendingUndoAction.previousJobs);
      setPendingUndoAction(null);
    }
    setActiveToast(null);
    setPageToast(null);
  };

  const confirmDeletePermanent = async () => {
    if (!pendingJobs.length) return;

    try {
      const targetIds = new Set(pendingJobs.map((job) => job.id));
      closeModal();
      await startUndoableAction({
        type: 'deletePermanent',
        applyLocalUpdate: (prev) =>
          prev.filter((item) => !targetIds.has(item.id)),
        commit: () =>
          Promise.all(pendingJobs.map((job) => deleteJobPostPermanent(job.id))),
      });
    } catch (error) {
      console.error('Failed to delete jobs permanently:', error);
    }
  };

  const confirmPublish = async () => {
    if (!pendingJob) return;

    try {
      await commitPendingUndoAction();
      await changeJobPostStatus(pendingJob.id, 'ACTIVE');
      closeModal();
      setActiveToast('publish');
      await fetchJobPosts();
    } catch (error) {
      console.error('Failed to publish job:', error);
    }
  };

  const actions = {
    activeToast,
    activeModal,
    handleUndo,
    closeToast,
    closeModal,
    confirmDeletePermanent,
    confirmPublish,
  };

  const triggerFilterLoading = () => {
    if (filterLoadingTimeoutRef.current) {
      clearTimeout(filterLoadingTimeoutRef.current);
    }

    setIsFilterLoading(true);
    filterLoadingTimeoutRef.current = setTimeout(() => {
      setIsFilterLoading(false);
      filterLoadingTimeoutRef.current = null;
    }, 250);
  };

  const handleSearch = (paramsOrTerm) => {
    triggerFilterLoading();
    setCurrentPage(1);
    if (isManager) {
      setAdminSearchTerm(typeof paramsOrTerm === 'string' ? paramsOrTerm : '');
      return;
    }
    setSearchParams({
      jobTitle: paramsOrTerm?.jobTitle || '',
      location: paramsOrTerm?.location || '',
    });
    setPendingTrack('search');
  };

  const handleFilterChange = (newFilters) => {
    triggerFilterLoading();
    setCurrentPage(1);
    setFilters((prev) => ({ ...prev, ...newFilters }));
    setPendingTrack('filter');
  };

  const handlePageChange = (page) => {
    triggerFilterLoading();
    setCurrentPage(page);
  };

  return (
    <div
      className={`flex flex-col items-center relative ${
        isMobileManager
          ? 'pb-[100px] bg-grayscale-gray00'
          : `pb-20 tabletV:pb-[180px] pt-6 ${isManager ? 'tabletV:pt-10' : 'tabletV:pt-[60px]'} px-4 tabletH:px-6 tabletV:px-10 bg-grayscale-gray01`
      }`}
    >
      {!isManager && (
      <div className='flex flex-col items-center max-w-[1166px] pb-10 pt-0 px-0 relative shrink-0 w-full'>
        <JobHeader />
          <SearchBar onSearch={handleSearch} jobs={jobs} />
      </div>
      )}

      {isMobileManager && (
        <>
          <div className='flex h-[54px] w-full items-center px-4 py-1.5'>
            <button
              type='button'
              onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}
              className='flex items-center gap-2 typo-title2 text-text-primary'
            >
              <NavigationLeftIcon className='size-6 shrink-0' />
              {t('Job postings', '채용 공고 관리')}
            </button>
          </div>
          <div className='flex w-full flex-col px-4 py-2'>
            <AdminSearchBar
              onSearch={handleSearch}
              defaultValue={adminSearchTerm}
              onOpenMenu={() => setIsAdminMenuOpen(true)}
            />
          </div>
          {/* 관리자: 기본 잡보드 피드와 같은 필터 칩 바 (비즈니스 계정은 필터 없음) */}
          {showFilterSidebar && (
            <div className='w-full border-b border-grayscale-gray01 px-4 pb-4 pt-6'>
              <FilterSidebar
                management
                onFilterChange={handleFilterChange}
                jobs={jobs}
              />
            </div>
          )}
          <AdminMobileMenuDrawer
            isOpen={isAdminMenuOpen}
            onClose={() => setIsAdminMenuOpen(false)}
            activeStatus={adminStatus}
            onChangeStatus={(nextStatus) => {
              setAdminStatus(nextStatus);
              setCurrentPage(1);
            }}
            counts={adminCounts}
            showAllJobs={isAdmin || isEmployer}
          />
        </>
      )}

      <div
        className={`flex flex-col tabletV:flex-row gap-4 tabletV:gap-6 items-stretch tabletV:items-start max-w-[1166px] relative shrink-0 w-full ${
          isMobileManager ? 'px-4 pt-4' : ''
        }`}
      >
        {showSideColumn && (
        <div className={`flex flex-col gap-6 w-full shrink-0 ${isManager ? 'tabletV:w-[270px]' : 'tabletV:w-[273px]'}`}>
          {isManager && !isMobile && (
            <AdminSidebar
              activeStatus={adminStatus}
              onChangeStatus={(nextStatus) => {
                setAdminStatus(nextStatus);
                setCurrentPage(1);
              }}
              counts={adminCounts}
              // All Jobs는 비즈니스·관리자 계정에만 노출
              showAllJobs={isAdmin || isEmployer}
            />
          )}
          {/* 비즈니스 계정의 공고 관리 화면에서는 필터를 노출하지 않는다 */}
          {showFilterSidebar && (
            <FilterSidebar collapsible={isManager} onFilterChange={handleFilterChange} jobs={jobs} />
          )}
        </div>
        )}

        <div
          className={`flex flex-[1_0_0] flex-col items-start min-h-px w-full min-w-0 tabletV:min-w-[643px] relative self-stretch ${
            'bg-transparent'
          }`}
        >
          {isManager && !isMobile && (
            <div className='w-full max-w-[600px] mb-[30px]'>
              <AdminSearchBar onSearch={handleSearch} defaultValue={adminSearchTerm} />
            </div>
          )}
          <div
            className={`relative flex items-start w-full shrink-0 ${
              isManager
                ? 'p-0'
                : 'px-0 pt-0 pb-4 tabletV:pb-10'
            }`}
          >
            <div
              className={`flex flex-[1_0_0] flex-col ${
                isMobileManager
                  ? 'gap-4'
                  : isManager
                  ? 'gap-6'
                  : 'gap-3 tabletV:gap-6'
              } items-center min-h-px min-w-px relative`}
            >
              <div className={`w-full ${isMobileManager ? 'px-[5px]' : ''}`}>
              <JobListHeader
                title={
                  isMobileManager && adminStatusItem
                    ? t(adminStatusItem.label[0], adminStatusItem.label[1])
                    : undefined
                }
                onManageJobs={!isManager && canManage ? () => navigate('/jobs/admin') : undefined}
                status={adminStatus}
                isAdmin={isManager}
                hideArchive={isEmployer}
                checked={isAllChecked}
                onToggleAll={handleToggleAll}
                selectedCount={selectedJobIds.size}
                totalCount={filteredJobs.length}
                isFilterApplied={isFilterApplied}
                sortBy={sortBy}
                onSortChange={(nextSortBy) => {
                  triggerFilterLoading();
                  setSortBy(nextSortBy);
                  setCurrentPage(1);
                }}
                onDeleteSelected={() => runBulkAction('delete')}
                onHideSelected={() => runBulkAction('hide')}
                onUnhideSelected={() => runBulkAction('unhide')}
                onDeletePermanentSelected={() =>
                  runBulkAction('deletePermanent')
                }
              />
              </div>

              {!isManager && canManage && <CreateJobBanner />}
              {isMobileManager && <CreateJobBanner variant='plain' />}

              {isLoading || isFilterLoading ? (
                Array.from({ length: PAGE_SIZE }).map((_, idx) => (
                  <SkeletonJobCard key={idx} isAdmin={isManager} />
                ))
              ) : filteredJobs.length === 0 ? (
                <SearchNotFound />
              ) : (
                pagedJobs.map((job, index) => (
                  <React.Fragment key={job.id}>
                    <CardComponent
                      job={job}
                      onSaveToast={() => setShowSaveToast(true)}
                      onToggleBookmark={handleToggleBookmark}
                      onToggleHidden={handleToggleHiddenForUser}
                      isAdmin={isManager}
                      checked={selectedJobIds.has(job.id)}
                      onToggle={(checked) => handleToggleOne(job.id, checked)}
                      onAction={handleCardAction}
                      isTrashView={adminStatus === 'trash'}
                    />
                    {/* 채용 담당자 진입점 — 모바일/데스크탑 공통 */}
                    {!isManager && index === HIRING_BANNER_INDEX && (
                      <HiringBanner />
                    )}

                    {/* Pathfinder 진입점 — 모바일/데스크탑 공통 */}
                    {!isManager && index === PATHFINDER_BANNER_INDEX && (
                      <KpathportInlineBanner />
                    )}
                  </React.Fragment>
                ))
              )}

              {filteredJobs.length > 0 && (
                <JobBoardPagination
                  current={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              )}

              {showToast && (
                <div className='fixed top-[94px] left-1/2 -translate-x-1/2 z-[1000]'>
                  <Toast
                    type='success'
                    state='white'
                    title={t(
                      'Your standard resume is ready',
                      '기본 이력서가 준비되었습니다',
                    )}
                    message={t('Start applying now', '지금 바로 지원해 보세요')}
                    hierarchy='mostImportant'
                    direction='down'
                  />
                </div>
              )}

              {showSaveToast && (
                <div className='fixed bottom-[30px] left-1/2 -translate-x-1/2 z-[1000] w-[calc(100vw-32px)] max-w-[400px] tabletV:w-auto tabletV:max-w-none'>
                  <Toast
                    type='success'
                    state='white'
                    title={t('Saved to your job', '공고를 저장했습니다')}
                    direction='up'
                    buttonLabel={t('View Saved Jobs', '저장한 공고 보기')}
                    onClose={() => setShowSaveToast(false)}
                    onButtonClick={() => navigate('/jobs/save')}
                    customWidth='380px'
                  />
                </div>
              )}

              {(actions.activeToast === 'delete' ||
                pageToast?.action === 'delete') && (
                <div className='fixed bottom-[30px] left-1/2 -translate-x-1/2 z-[1000] w-[calc(100vw-32px)] max-w-[400px] tabletV:w-auto tabletV:max-w-none'>
                  <Toast
                    type='success'
                    title={t(
                      'Job moved to Trash',
                      '공고를 휴지통으로 이동했습니다',
                    )}
                    buttonLabel={t('Undo', '실행 취소')}
                    onButtonClick={actions.handleUndo}
                    onClose={actions.closeToast}
                    customWidth='w-[340px]'
                  />
                </div>
              )}
              {(actions.activeToast === 'hide' ||
                pageToast?.action === 'hide') && (
                <div className='fixed bottom-[30px] left-1/2 -translate-x-1/2 z-[1000] w-[calc(100vw-32px)] max-w-[400px] tabletV:w-auto tabletV:max-w-none'>
                  <Toast
                    type='success'
                    title={t('Job hidden', '공고를 숨겼습니다')}
                    buttonLabel={t('Undo', '실행 취소')}
                    onButtonClick={actions.handleUndo}
                    onClose={actions.closeToast}
                    customWidth='w-[260px]'
                  />
                </div>
              )}
              {(actions.activeToast === 'unhide' ||
                pageToast?.action === 'unhide') && (
                <div className='fixed bottom-[30px] left-1/2 -translate-x-1/2 z-[1000] w-[calc(100vw-32px)] max-w-[400px] tabletV:w-auto tabletV:max-w-none'>
                  <Toast
                    type='success'
                    title={t(
                      'Job is now active',
                      '공고가 게시 상태로 전환되었습니다',
                    )}
                    onClose={actions.closeToast}
                    customWidth='w-[240px]'
                  />
                </div>
              )}
              {(actions.activeToast === 'close' ||
                pageToast?.action === 'close') && (
                <div className='fixed bottom-[30px] left-1/2 -translate-x-1/2 z-[1000] w-[calc(100vw-32px)] max-w-[400px] tabletV:w-auto tabletV:max-w-none'>
                  <Toast
                    type='success'
                    title={t('Job closed', '공고를 마감했습니다')}
                    buttonLabel={t('Undo', '실행 취소')}
                    onButtonClick={actions.handleUndo}
                    onClose={actions.closeToast}
                    customWidth='w-[260px]'
                  />
                </div>
              )}
              {(actions.activeToast === 'restore' ||
                pageToast?.action === 'restore') && (
                <div className='fixed bottom-[30px] left-1/2 -translate-x-1/2 z-[1000] w-[calc(100vw-32px)] max-w-[400px] tabletV:w-auto tabletV:max-w-none'>
                  <Toast
                    type='success'
                    title={t('Job restored', '공고를 복원했습니다')}
                    onClose={actions.closeToast}
                    customWidth='w-[240px]'
                  />
                </div>
              )}
              {(actions.activeToast === 'reopen' ||
                pageToast?.action === 'reopen') && (
                <div className='fixed bottom-[30px] left-1/2 -translate-x-1/2 z-[1000] w-[calc(100vw-32px)] max-w-[400px] tabletV:w-auto tabletV:max-w-none'>
                  <Toast
                    type='success'
                    title={t('Job reopened', '공고를 다시 게시했습니다')}
                    onClose={actions.closeToast}
                    customWidth='w-[220px]'
                  />
                </div>
              )}
              {(actions.activeToast === 'restoreToActive' ||
                pageToast?.action === 'restoreToActive') && (
                <div className='fixed bottom-[30px] left-1/2 -translate-x-1/2 z-[1000] w-[calc(100vw-32px)] max-w-[400px] tabletV:w-auto tabletV:max-w-none'>
                  <Toast
                    type='success'
                    title={t(
                      'Job restored to Active',
                      '공고가 게시 상태로 복원되었습니다',
                    )}
                    onClose={actions.closeToast}
                    customWidth='w-[260px]'
                  />
                </div>
              )}
              {(actions.activeToast === 'duplicate' ||
                pageToast?.action === 'duplicate') && (
                <div className='fixed bottom-[30px] left-1/2 -translate-x-1/2 z-[1000] w-[calc(100vw-32px)] max-w-[400px] tabletV:w-auto tabletV:max-w-none'>
                  <Toast
                    type='success'
                    title={t('Job duplicated', '공고를 복제했습니다')}
                    onClose={actions.closeToast}
                    customWidth='w-[220px]'
                  />
                </div>
              )}
              {(actions.activeToast === 'deletePermanent' ||
                pageToast?.action === 'deletePermanent') && (
                <div className='fixed bottom-[30px] left-1/2 -translate-x-1/2 z-[1000] w-[calc(100vw-32px)] max-w-[400px] tabletV:w-auto tabletV:max-w-none'>
                  <Toast
                    hierarchy='mostImportant'
                    type='success'
                    title={t(
                      'Job permanently deleted',
                      '공고를 영구 삭제했습니다',
                    )}
                    buttonLabel={t('Undo', '실행 취소')}
                    onButtonClick={actions.handleUndo}
                    onClose={actions.closeToast}
                    customWidth='w-[400px]'
                  />
                </div>
              )}
              {(actions.activeToast === 'setActive' ||
                pageToast?.action === 'setActive') && (
                <div className='fixed bottom-[30px] left-1/2 -translate-x-1/2 z-[1000] w-[calc(100vw-32px)] max-w-[400px] tabletV:w-auto tabletV:max-w-none'>
                  <Toast
                    type='success'
                    title={t(
                      'Job is now active',
                      '공고가 게시 상태로 전환되었습니다',
                    )}
                    onClose={actions.closeToast}
                    customWidth='w-[240px]'
                    onButtonClick={actions.handleUndo}
                  />
                </div>
              )}
              {(actions.activeToast === 'publish' ||
                pageToast?.action === 'publish') && (
                <div className='fixed bottom-[30px] left-1/2 -translate-x-1/2 z-[1000] w-[calc(100vw-32px)] max-w-[400px] tabletV:w-auto tabletV:max-w-none'>
                  <Toast
                    type='success'
                    title={t('Job published', '공고를 게시했습니다')}
                    onClose={actions.closeToast}
                    customWidth='w-[220px]'
                  />
                </div>
              )}

              {actions.activeModal === 'deletePermanent' && (
                <Modal
                  isOpen
                  title={t(
                    'Delete this job permanently?',
                    '이 공고를 영구 삭제할까요?',
                  )}
                  subText={t(
                    'This job will be permanently deleted and cannot be restored',
                    '영구 삭제된 공고는 복원할 수 없습니다',
                  )}
                  onClickLeftBtn={actions.closeModal}
                  onClickRightBtn={actions.confirmDeletePermanent}
                  rightButtonLabel={t('Delete permanently', '영구 삭제')}
                />
              )}
              {actions.activeModal === 'publish' && (
                <Modal
                  isOpen
                  title={t('Publish this job?', '이 공고를 게시할까요?')}
                  subText={t(
                    'Once published, this job will become active and visible to users',
                    '게시하면 공고가 활성화되어 사용자에게 노출됩니다',
                  )}
                  onClickLeftBtn={actions.closeModal}
                  onClickRightBtn={actions.confirmPublish}
                  rightButtonLabel={t('Publish job', '공고 게시')}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobBoard;
